import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, offset, autoUpdate } from '@floating-ui/react-dom';
import { useSignal } from '@preact/signals-react';
import styled from 'styled-components';
import { MenuItem, Loader } from 'components';
import { Container } from 'components/common/dropDownWrapper/DropDownWrapper';
import { Pause, PauseAction, Start, StartIcon } from 'assets/icons';
import { campaignReportsAPI } from 'services/campaignReports.service';

const PAUSED_STATUS = 3;
const ACTIVE_STATUS = 0;

export const CampaignStatusAction = ({ campaignId, status = ACTIVE_STATUS }) => {
  const isOpen$ = useSignal(false);
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(8)],
    whileElementsMounted: autoUpdate,
  });
  const [updateCampaignStatus, result] = campaignReportsAPI.useUpdateCampaignStatusMutation();

  const isPaused = +status === PAUSED_STATUS;
  const Icon = isPaused ? Pause : Start;
  const actionLabel = isPaused ? 'Play Now' : 'Pause Now';
  const ActionIcon = isPaused ? StartIcon : PauseAction;
  const nextStatus = isPaused ? ACTIVE_STATUS : PAUSED_STATUS;

  useEffect(() => {
    if (!isOpen$.value) return;

    const closeOnOutsideClick = (event) => {
      if (
        refs.reference.current?.contains(event.target) ||
        refs.floating.current?.contains(event.target)
      )
        return;

      isOpen$.value = false;
    };
    const closeOnScroll = () => {
      isOpen$.value = false;
    };

    document.addEventListener('click', closeOnOutsideClick, true);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('click', closeOnOutsideClick, true);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [isOpen$.value, refs, isOpen$]);

  const statusAction = useCallback(() => {
    isOpen$.value = false;
    updateCampaignStatus({
      campaignId,
      status: nextStatus,
    });
  }, [campaignId, nextStatus]);

  return (
    <Wrap ref={refs.setReference}>
      {result.isLoading && <Loader.Global delay={1000} />}
      <IconButton
        type='button'
        onClick={() => (isOpen$.value = !isOpen$.value)}
        isOpen={isOpen$.value}
      >
        <Icon />
      </IconButton>
      {isOpen$.value &&
        createPortal(
          <Container ref={refs.setFloating} isOpen width={180} style={floatingStyles}>
            <MenuItem
              height={40}
              icon={ActionIcon}
              value={actionLabel}
              id={isPaused ? 'play_now' : 'pause_now'}
              onClick={statusAction}
            >
              {actionLabel}
            </MenuItem>
          </Container>,
          document.body,
        )}
    </Wrap>
  );
};

const Wrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`;
